import { AxiosResponse } from 'axios';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { isProductionEnvironment, isStageEnvironment } from 'utils/environment';
import {
  updateFeedReserveSettings,
  updateConsumptionRate,
  updateManualInventory,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/api/update-ofd-settings';
import {
  UpdateFeedReserveInput,
  UpdateConsumptionRateInput,
  UpdateManualInventoryInput,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings-input.type';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';

export const useUpdateOFDSettings = () => {
  const params = useParams<MySupplyPlanParams>();
  const snackbarDispatch = useSnackbarDispatch();
  const intl = useIntl();
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ['getTimeline']
    });
    queryClient.invalidateQueries({
      queryKey: ['getRecommendationAssortment']
    });
  };

  const updateFeedInventory = usePost<AxiosResponse, UpdateManualInventoryInput>(
    (request) => updateManualInventory(request, params.globalSiteId),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      invalidateQueries();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: {
          msg: intl.formatMessage({ id: 'snackbar.errors.default' }),
          error: error,
        },
      });
    }
  );

  const updateFeedReserve = usePost<AxiosResponse, UpdateFeedReserveInput>(
    (request) => updateFeedReserveSettings(request, params.globalSiteId),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      invalidateQueries();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: {
          msg: intl.formatMessage({ id: 'snackbar.errors.default' }),
          error: error,
        },
      });
    }
  );

  const updateDailyConsumptionRate = usePost<AxiosResponse, UpdateConsumptionRateInput>(
    (request) => updateConsumptionRate(request, params.globalSiteId),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      invalidateQueries();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: {
          msg: intl.formatMessage({ id: 'snackbar.errors.default' }),
          error: error,
        },
      });
    }
  );

  return { updateFeedInventory, updateFeedReserve, updateDailyConsumptionRate };
};
